<template>
    <div class="w-full flex flex-col items-start relative">
        <div class="w-full mt-1 flex">
            <div :class="[hideLateralDescriptionValue ? 'w-full' : 'w-1/2 pr-2', inputClass]">
                <Label :for="config.display_name" :class="['mb-2', overrideLabelOpacity ? 'override-opacity' : '']">
                    {{ config.display_name }}
                </Label>
                <MultiSelect v-model="controlValue" :options="options" placeholder="config.placeholder || 'Select options'" />
                <div v-if="errors.length" class="text-destructive mt-1 text-xs">
                    <template v-for="error in errors" :key="error.$uid">
                        <span>* {{ error.$message }}</span>
                    </template>
                </div>
            </div>
            <div v-if="!hideLateralDescriptionValue" class="w-1/2 pl-2">
                <div
                    v-if="
                        hideLateralDescriptionValue &&
                        config.display_name?.trim().toLowerCase() !== config.description?.trim().toLowerCase() &&
                        config.description?.trim().split(' ').length > 1
                    "
                    class="flex flex-col items-center group">
                    <Tooltip v-if="config.description && hideLateralDescriptionValue">
                        <TooltipTrigger asChild>
                            <span><Info class="h-4 w-4 text-muted-foreground" /></span>
                        </TooltipTrigger>
                        <TooltipContent side="left" class="whitespace-normal max-w-xs">
                            <p class="text-xs" v-html="sanitizeHtml(config.description)" />
                        </TooltipContent>
                    </Tooltip>
                </div>
                <Alert
                    v-if="config.description && !hideLateralDescriptionValue"
                    variant="default"
                    class="min-h-[calc(theme(spacing.10)+theme(spacing.6))] flex items-center p-3 text-xs border bg-muted/10"
                    :class="errors.length ? 'mb-5' : ''">
                    <AlertDescription class="text-muted-foreground text-2xs">
                        <span v-html="sanitizeHtml(config.description)" />
                    </AlertDescription>
                </Alert>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUpdated, onUnmounted } from 'vue';
import { sanitizeHtml } from '@/utils/utils';
import { MultiSelect } from '@/components/ui/multi-select';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Info } from 'lucide-vue-next';
import _ from 'lodash';

const props = defineProps({
    config: { type: Object, required: true },
    value: { type: Array, default: () => [] },
    editMode: { type: Boolean, default: true },
    overrideLabelOpacity: { type: Boolean, default: false },
    hideLateralDescription: { type: Boolean, default: false },
    errors: { type: Array, default: () => [] },
    inputClass: { type: String, default: '' }
});
const emit = defineEmits(['input']);

// Ensure controlValue is always an array.
const controlValue = ref(Array.isArray(props.value) ? props.value : Array.isArray(props.config.value.default) ? props.config.value.default : []);

// Compute options from config.value.raw_values.
const options = computed(() => {
    if (Array.isArray(props.config.value.raw_values)) {
        return props.config.value.raw_values.map((opt) => (typeof opt === 'object' ? opt : { label: opt, value: opt }));
    }
    return [];
});

// Compute the selected label based on controlValue length.
const selectedLabel = computed(() => {
    const count = controlValue.value.length;
    return count === 0 ? '' : `${count} item${count > 1 ? 's' : ''} selected`;
});

// Setup hideLateralDescriptionValue similar to the original.
const hideLateralDescriptionValue = ref(props.hideLateralDescription);
const handleResize = () => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : window.innerWidth <= 1024;
};

window.addEventListener('resize', handleResize);
onMounted(() => {
    handleResize();
});
onUpdated(() => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : window.innerWidth <= 1024;
});
onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

// Emit changes when controlValue is updated.
watch(controlValue, (newVal) => {
    emit('input', newVal);
});
</script>

<style scoped></style>
